import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { D1_SCHOOLS } from '../data/d1-schools';
import { ChevronDown, CheckCircle2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = ['Athletic Department Admin', 'Compliance / Risk', 'NIL Director', 'Coach', 'Agent / Representative', 'Athlete', 'Parent / Family', 'Other'];
const SPORTS = ['Football', "Men's Basketball", "Women's Basketball", 'Baseball', 'Ice Hockey'];
const POSITIONS_BY_SPORT = {
  'Football': ['Quarterback', 'Running Back', 'Tight End', 'Wide Receiver', 'Linebacker', 'Offensive Line', 'Defensive Line', 'Safety', 'Cornerback', 'Kicker', 'Punter'],
  "Men's Basketball": ['Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center'],
  "Women's Basketball": ['Guard', 'Forward', 'Center'],
  'Baseball': ['Pitcher', 'Catcher', 'Infielder', 'Outfielder'],
  'Ice Hockey': ['Forward', 'Defenseman', 'Goaltender'],
};
const ROLE_DEPENDENT_SPORTS = ['Football', "Women's Basketball"];

const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

const SelectField = ({ label, value, onChange, options, placeholder, required, disabled, testId }) => (
  <div>
    <label className="block text-sm font-medium text-[#111827] mb-1.5">{label}{required && <span className="text-red-500">*</span>}</label>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full h-12 px-4 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] appearance-none focus:outline-none focus:border-[#4CD6BE] focus:ring-2 focus:ring-[#4CD6BE]/30 disabled:bg-[#F3F4F6] disabled:text-[#9CA3AF] transition"
        data-testid={testId}
      >
        <option value="">{placeholder}</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF] pointer-events-none" />
    </div>
  </div>
);

const InputField = ({ label, type = 'text', value, onChange, placeholder, required, min, testId, helpText }) => (
  <div>
    <label className="block text-sm font-medium text-[#111827] mb-1.5">{label}{required && <span className="text-red-500">*</span>}</label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      min={min}
      className="w-full h-12 px-4 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] focus:outline-none focus:border-[#4CD6BE] focus:ring-2 focus:ring-[#4CD6BE]/30 transition"
      data-testid={testId}
    />
    {helpText && <p className="text-xs text-[#9CA3AF] mt-1">{helpText}</p>}
  </div>
);

// ── SCREENS ──

const QuoteFormPage = () => {
  const [screen, setScreen] = useState('form'); // form | estimate | declined | confirmed
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null); // { requestId, pricing }

  // Form state
  const [requester, setRequester] = useState({ name: '', email: '', phone: '', role: '' });
  const [athlete, setAthlete] = useState({ name: '', school: '', sport: '', position: '', coverageAmount: '', starterStatus: '' });
  const [underwriting, setUnderwriting] = useState({
    injuriesPast24Months: '', multipleAclTears: false, recentSurgery: false,
    coverage: '', details: '', dates: ''
  });

  const positions = useMemo(() => POSITIONS_BY_SPORT[athlete.sport] || [], [athlete.sport]);
  const showRole = ROLE_DEPENDENT_SPORTS.includes(athlete.sport);

  useEffect(() => {
    setAthlete(a => ({ ...a, position: '', starterStatus: '' }));
  }, [athlete.sport]);

  useEffect(() => {
    if (underwriting.injuriesPast24Months !== 'Yes') {
      setUnderwriting(u => ({ ...u, multipleAclTears: false, recentSurgery: false, coverage: '', details: '', dates: '' }));
    }
  }, [underwriting.injuriesPast24Months]);

  const validate = () => {
    if (!requester.name || !requester.email || !requester.phone || !requester.role) return 'Please fill in all requester fields.';
    if (!athlete.name || !athlete.school || !athlete.sport || !athlete.position) return 'Please fill in all athlete fields.';
    const nilVal = Number(athlete.coverageAmount);
    if (!nilVal || nilVal < 10000 || nilVal > 50000000) return 'NIL contract value must be between $10,000 and $50,000,000.';
    if (showRole && !athlete.starterStatus) return 'Please select Starter or Backup.';
    if (!underwriting.injuriesPast24Months) return 'Please answer the injury question.';
    if (underwriting.injuriesPast24Months === 'Yes') {
      if (!underwriting.coverage) return 'Please select injury coverage preference.';
      if (!underwriting.details || underwriting.details.length < 4) return 'Please provide injury details (min 4 characters).';
      if (!underwriting.dates || underwriting.dates.length < 2) return 'Please provide approximate injury dates.';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }
    setError('');
    setSubmitting(true);
    try {
      const payload = {
        requester,
        athlete: { ...athlete, coverageAmount: Number(athlete.coverageAmount) },
        underwriting
      };
      const res = await fetch(`${API}/quotes/submit`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail || 'Submission failed'); }
      const data = await res.json();
      setResult(data);
      // Request-a-quote lead: auto-confirm and route to the team (no instant pricing)
      await fetch(`${API}/quotes/confirm`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ request_id: data.requestId })
      });
      setScreen('confirmed');
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setRequester({ name: '', email: '', phone: '', role: '' });
    setAthlete({ name: '', school: '', sport: '', position: '', coverageAmount: '', starterStatus: '' });
    setUnderwriting({ injuriesPast24Months: '', multipleAclTears: false, recentSurgery: false, coverage: '', details: '', dates: '' });
    setResult(null);
    setError('');
    setScreen('form');
    window.scrollTo(0, 0);
  };

  // ── NAV ──
  const Nav = () => (
    <nav className="border-b border-[#E5E7EB]">
      <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/insurance" className="flex items-center gap-2">
          <img src="/nil-resources/logos/logo-transparent.png" alt="Players Health" className="h-8" />
        </Link>
        {screen === 'form' && (
          <Link to="/insurance" className="text-[#6B7280] text-sm hover:text-[#0F172A] flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        )}
      </div>
    </nav>
  );

  // ── FORM SCREEN ──
  if (screen === 'form') return (
    <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Nav />
      <form onSubmit={handleSubmit} className="max-w-3xl mx-auto px-6 py-10">
        {/* Section 1: Your Info */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-8 rounded-full bg-[#EEF0FF] flex items-center justify-center text-[#6B5CE7] text-xs font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>01</span>
            <div>
              <h2 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>Your info</h2>
              <p className="text-sm text-[#6B7280]">So we know who to call.</p>
            </div>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-4 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
            <div className="grid md:grid-cols-2 gap-4">
              <InputField label="Full name" value={requester.name} onChange={v => setRequester({...requester, name: v})} required testId="req-name" />
              <InputField label="Email" type="email" value={requester.email} onChange={v => setRequester({...requester, email: v})} required testId="req-email" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <InputField label="Phone" type="tel" value={requester.phone} onChange={v => setRequester({...requester, phone: v})} required testId="req-phone" />
              <SelectField label="Your role" value={requester.role} onChange={v => setRequester({...requester, role: v})} options={ROLES} placeholder="Pick one" required testId="req-role" />
            </div>
          </div>
        </div>

        {/* Section 2: Athlete Details */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-8 rounded-full bg-[#EEF0FF] flex items-center justify-center text-[#6B5CE7] text-xs font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>02</span>
            <div>
              <h2 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>Athlete details</h2>
              <p className="text-sm text-[#6B7280]">Tell us about the athlete.</p>
            </div>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-4 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
            <InputField label="Athlete name" value={athlete.name} onChange={v => setAthlete({...athlete, name: v})} required testId="ath-name" />
            <div className="grid md:grid-cols-2 gap-4">
              <SelectField label="School (D1)" value={athlete.school} onChange={v => setAthlete({...athlete, school: v})} options={D1_SCHOOLS} placeholder="Pick a school" required testId="ath-school" />
              <SelectField label="Sport" value={athlete.sport} onChange={v => setAthlete({...athlete, sport: v})} options={SPORTS} placeholder="Pick a sport" required testId="ath-sport" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <SelectField label="Position" value={athlete.position} onChange={v => setAthlete({...athlete, position: v})} options={positions} placeholder="Pick a position" required disabled={!athlete.sport} testId="ath-position" />
              <InputField label="NIL contract value (USD)" type="number" value={athlete.coverageAmount} onChange={v => setAthlete({...athlete, coverageAmount: v})} required min={10000} testId="ath-nil" helpText="Best estimate of total annual NIL contract value." />
            </div>
            {showRole && (
              <SelectField label="Role" value={athlete.starterStatus} onChange={v => setAthlete({...athlete, starterStatus: v})} options={['Starter', 'Backup']} placeholder="Pick one" required testId="ath-role" />
            )}
          </div>
        </div>

        {/* Section 3: Underwriting */}
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-8 h-8 rounded-full bg-[#EEF0FF] flex items-center justify-center text-[#6B5CE7] text-xs font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>03</span>
            <div>
              <h2 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>Underwriting</h2>
              <p className="text-sm text-[#6B7280]">A few quick questions. Be honest — material misstatements void a policy.</p>
            </div>
          </div>
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 space-y-5 shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
            <div>
              <label className="block text-sm font-medium text-[#111827] mb-3">Significant injuries in the past 24 months?<span className="text-red-500">*</span></label>
              <div className="flex gap-3">
                {['No', 'Yes', 'Unknown'].map(opt => (
                  <button key={opt} type="button"
                    onClick={() => setUnderwriting({...underwriting, injuriesPast24Months: opt})}
                    className={`px-5 py-2.5 rounded-lg text-sm font-medium border transition ${
                      underwriting.injuriesPast24Months === opt
                        ? 'bg-[#0F172A] text-white border-[#0F172A]'
                        : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-[#9CA3AF]'
                    }`}
                    data-testid={`injury-${opt.toLowerCase()}`}
                  >{opt}</button>
                ))}
              </div>
            </div>

            {underwriting.injuriesPast24Months === 'Yes' && (
              <div className="space-y-4 pl-4 border-l-2 border-[#E5E7EB]">
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-[#111827]">Multiple ACL tears in injury history?</span>
                  <button type="button"
                    onClick={() => setUnderwriting({...underwriting, multipleAclTears: !underwriting.multipleAclTears})}
                    className={`w-12 h-6 rounded-full transition-colors relative ${underwriting.multipleAclTears ? 'bg-[#EF4444]' : 'bg-[#E5E7EB]'}`}
                    data-testid="toggle-acl"
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${underwriting.multipleAclTears ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-sm text-[#111827]">Surgery in past 12 months?</span>
                  <button type="button"
                    onClick={() => setUnderwriting({...underwriting, recentSurgery: !underwriting.recentSurgery})}
                    className={`w-12 h-6 rounded-full transition-colors relative ${underwriting.recentSurgery ? 'bg-[#EF4444]' : 'bg-[#E5E7EB]'}`}
                    data-testid="toggle-surgery"
                  >
                    <div className={`w-5 h-5 bg-white rounded-full shadow absolute top-0.5 transition-transform ${underwriting.recentSurgery ? 'translate-x-6' : 'translate-x-0.5'}`} />
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-2">Injury coverage preference<span className="text-red-500">*</span></label>
                  <div className="flex gap-3">
                    {[
                      { val: 'Included', label: 'Included (+30% load)' },
                      { val: 'Excluded', label: 'Excluded (+10% load)' },
                    ].map(opt => (
                      <button key={opt.val} type="button"
                        onClick={() => setUnderwriting({...underwriting, coverage: opt.val})}
                        className={`flex-1 px-4 py-2.5 rounded-lg text-sm font-medium border transition ${
                          underwriting.coverage === opt.val
                            ? 'bg-[#0F172A] text-white border-[#0F172A]'
                            : 'bg-white text-[#374151] border-[#E5E7EB] hover:border-[#9CA3AF]'
                        }`}
                        data-testid={`coverage-${opt.val.toLowerCase()}`}
                      >{opt.label}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-1.5">Injury details<span className="text-red-500">*</span></label>
                  <textarea value={underwriting.details} onChange={e => setUnderwriting({...underwriting, details: e.target.value})} rows={3}
                    className="w-full px-4 py-3 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] focus:outline-none focus:border-[#4CD6BE] focus:ring-2 focus:ring-[#4CD6BE]/30 transition"
                    data-testid="injury-details" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#111827] mb-1.5">Approximate dates<span className="text-red-500">*</span></label>
                  <textarea value={underwriting.dates} onChange={e => setUnderwriting({...underwriting, dates: e.target.value})} rows={2}
                    className="w-full px-4 py-3 bg-white border border-[#E5E7EB] rounded-lg text-sm text-[#111827] focus:outline-none focus:border-[#4CD6BE] focus:ring-2 focus:ring-[#4CD6BE]/30 transition"
                    data-testid="injury-dates" />
                </div>
              </div>
            )}
          </div>
        </div>

        {error && <div className="bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-4 mb-6 text-[#991B1B] text-sm" data-testid="form-error">{error}</div>}

        <p className="text-xs text-[#9CA3AF] mb-4">By submitting, you agree to be contacted by a Players Health advisor about your quote.</p>
        <button type="submit" disabled={submitting}
          className="w-full h-12 bg-[#4CD6BE] text-[#0F172A] font-semibold rounded-md hover:bg-[#3BC0A9] transition disabled:opacity-50 text-base"
          data-testid="submit-quote"
        >{submitting ? 'Submitting...' : 'Request a Quote'}</button>
      </form>
    </div>
  );

  // ── CONFIRMED SCREEN ──
  if (screen === 'confirmed') {
    const firstName = requester.name.split(' ')[0];
    return (
      <div className="min-h-screen bg-[#F9FAFB]" style={{ fontFamily: "'Inter', sans-serif" }}>
        <Nav />
        <div className="max-w-3xl mx-auto px-6 py-10">
          <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.08)] mb-8">
            <div className="h-1 bg-gradient-to-r from-[#6B5CE7] via-[#4ECDC4] to-[#FFD93D]" />
            <div className="p-8">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-6 h-6 text-[#10B981]" />
                <p className="text-[#065F46] text-sm font-semibold">You're confirmed</p>
              </div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-[#0F172A] mb-4" style={{ fontFamily: "'Barlow Condensed', sans-serif", textTransform: 'uppercase' }}>
                We're on it.
              </h1>
              <p className="text-[#6B7280] text-base leading-relaxed">
                Thanks, <strong>{firstName}</strong>. A licensed Players Health advisor will reach out to <strong>{requester.email}</strong> within 1–2 business days to review your submission and put together your quote.
              </p>
            </div>
            <div className="mx-8 flex items-center gap-2 pb-6">
              <span className="text-sm text-[#6B7280]">Reference ID</span>
              <code className="bg-[#F3F4F6] px-3 py-1 rounded text-sm text-[#0F172A] font-mono">{result.requestId}</code>
            </div>
            <div className="bg-[#F9FAFB] px-8 py-6 border-t border-[#E5E7EB]">
              <p className="text-xs text-[#9CA3AF] font-semibold uppercase tracking-wider mb-4">What happens next</p>
              <div className="space-y-4">
                {[
                  { num: '1', when: 'Today', title: 'Your confirmation is in', desc: 'We received it and the file is queued for an advisor.' },
                  { num: '2', when: 'Within 1 business day', title: 'Advisor assigned', desc: 'You\u2019ll get a brief intro email with the advisor\u2019s name and direct line.' },
                  { num: '3', when: 'Within 2 business days', title: 'Official quote delivered', desc: 'Final premium, policy terms, and a signature link — ready to bind.' },
                ].map(s => (
                  <div key={s.num} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-7 h-7 rounded-full bg-[#EEF0FF] flex items-center justify-center text-[#6B5CE7] text-xs font-bold">{s.num}</div>
                      {s.num !== '3' && <div className="w-px h-full bg-[#E5E7EB] mt-1" />}
                    </div>
                    <div className="pb-4">
                      <p className="text-xs text-[#9CA3AF] mb-0.5">{s.when}</p>
                      <p className="text-sm font-semibold text-[#0F172A]">{s.title}</p>
                      <p className="text-sm text-[#6B7280]">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Contact block */}
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.08)] text-center mb-8">
            <p className="text-sm font-semibold text-[#0F172A] mb-1">Questions in the meantime?</p>
            <p className="text-sm text-[#6B7280] mb-3">Just reply, or reach us directly</p>
            <a href="mailto:hello@playershealth.com" className="text-[#6B5CE7] text-sm font-medium hover:underline">hello@playershealth.com</a>
            <p className="text-xs text-[#9CA3AF] mt-1">Mon–Fri · 9a–6p ET</p>
          </div>

          <SubmissionSummary requester={requester} athlete={athlete} underwriting={underwriting} />

          <div className="flex gap-4 mt-8">
            <Link to="/insurance" className="text-sm text-[#6B7280] hover:underline">Back to home</Link>
            <button onClick={resetForm} className="text-sm text-[#6B5CE7] font-medium hover:underline">Submit another request</button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// ── SUBMISSION SUMMARY ──
const SubmissionSummary = ({ requester, athlete, underwriting }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
    <div className="px-6 py-4 border-b border-[#E5E7EB] bg-[#F9FAFB]">
      <h3 className="text-sm font-semibold text-[#0F172A]">Submission summary</h3>
      <p className="text-xs text-[#9CA3AF]">What we received. If anything looks off, reply to the email we send and we'll update it.</p>
    </div>
    <div className="divide-y divide-[#E5E7EB]">
      <SummarySection title="Requester" rows={[
        ['Name', requester.name], ['Email', requester.email], ['Phone', requester.phone], ['Role', requester.role]
      ]} />
      <SummarySection title="Athlete" rows={[
        ['Name', athlete.name], ['School', athlete.school], ['Sport', athlete.sport], ['Position', athlete.position],
        ['NIL value', formatCurrency(Number(athlete.coverageAmount))],
        ...(athlete.starterStatus ? [['Role', athlete.starterStatus]] : [])
      ]} />
      <SummarySection title="Underwriting" rows={[
        ['Significant injuries (24 mo)', underwriting.injuriesPast24Months],
        ...(underwriting.injuriesPast24Months === 'Yes' ? [
          ['Coverage preference', underwriting.coverage],
          ['Multiple ACL tears', underwriting.multipleAclTears ? 'Yes' : 'No'],
          ...(underwriting.recentSurgery ? [['Recent surgery', 'Yes']] : []),
          ...(underwriting.details ? [['Details', underwriting.details]] : []),
          ...(underwriting.dates ? [['Dates', underwriting.dates]] : []),
        ] : [])
      ]} />
    </div>
  </div>
);

const SummarySection = ({ title, rows }) => (
  <div className="px-6 py-4">
    <p className="text-xs text-[#9CA3AF] font-semibold uppercase tracking-wider mb-3">{title}</p>
    <div className="space-y-2">
      {rows.map(([label, val], i) => (
        <div key={i} className="flex justify-between text-sm">
          <span className="text-[#6B7280]">{label}</span>
          <span className="text-[#0F172A] font-medium text-right max-w-[60%]">{val}</span>
        </div>
      ))}
    </div>
  </div>
);

export default QuoteFormPage;
